/**
 * Phase 98 — Sélecteur manuel de lots Conthey pour une ligne de tournée.
 *
 * Affiche la liste des lots dispos au central pour ce produit, triés FIFO :
 *  - Checkbox par lot
 *  - Input qty (limité par la qty dispo du lot)
 *  - Badge DLC + "DLC courte" en rouge si is_dlc_short
 *  - Bouton "FIFO auto" qui pré-coche les lots FIFO pour couvrir qty_target
 *  - Validation : somme des qtys cochées <= qty_target (peut être <= pour "partiel")
 */
import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Package, AlertTriangle, Zap } from "lucide-react";
import { toast } from "sonner";

function fmtDlc(dlc) {
  if (!dlc) return "—";
  try {
    return new Date(dlc).toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch {
    return dlc.slice(0, 10);
  }
}

export function LotAllocationDialog({
  open,
  onOpenChange,
  centralDepotId,
  productId,
  productName,
  slotNumber,
  qtyTarget,
  initialAllocations = [],
  onConfirm,
}) {
  const [loading, setLoading] = useState(false);
  const [lots, setLots] = useState([]);
  // Map lot_id → qty (0 = non sélectionné)
  const [picks, setPicks] = useState({});

  useEffect(() => {
    if (!open || !centralDepotId || !productId) return;
    setLoading(true);
    api.get(`/refill-tours/lots-available`, { params: { central_depot_id: centralDepotId, product_id: productId } })
      .then(({ data }) => {
        setLots(data.lots || []);
        // Init picks avec les allocations existantes ou FIFO par défaut
        const initMap = {};
        if (initialAllocations && initialAllocations.length > 0) {
          initialAllocations.forEach((a) => { initMap[a.lot_id] = Number(a.qty || 0); });
        } else if (qtyTarget > 0) {
          // FIFO auto
          let remaining = qtyTarget;
          for (const lot of data.lots || []) {
            if (remaining <= 0) break;
            const take = Math.min(lot.quantity, remaining);
            if (take > 0) {
              initMap[lot.lot_id] = take;
              remaining -= take;
            }
          }
        }
        setPicks(initMap);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Erreur chargement lots"))
      .finally(() => setLoading(false));
  }, [open, centralDepotId, productId, qtyTarget, initialAllocations]);

  const totalPicked = useMemo(
    () => Object.values(picks).reduce((s, q) => s + Number(q || 0), 0),
    [picks]
  );
  const missing = Math.max(0, qtyTarget - totalPicked);
  const overShoot = Math.max(0, totalPicked - qtyTarget);

  const setLotQty = (lot_id, qty, max) => {
    const clamped = Math.max(0, Math.min(Number(qty || 0), max));
    setPicks((p) => ({ ...p, [lot_id]: clamped }));
  };

  const applyFifoAuto = () => {
    const auto = {};
    let remaining = qtyTarget;
    for (const lot of lots) {
      if (remaining <= 0) break;
      const take = Math.min(lot.quantity, remaining);
      if (take > 0) {
        auto[lot.lot_id] = take;
        remaining -= take;
      }
    }
    setPicks(auto);
  };

  const handleConfirm = () => {
    const allocations = lots
      .filter((l) => Number(picks[l.lot_id] || 0) > 0)
      .map((l) => ({
        lot_id: l.lot_id,
        lot_number: l.lot_number || "",
        dlc: l.dlc || null,
        qty: Number(picks[l.lot_id]),
      }));
    onConfirm(allocations);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="lot-allocation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-[#1F4E3D]" />
            Choix des lots — {productName || "Produit"}
            {slotNumber && (
              <Badge className="bg-[#1F4E3D] text-white font-mono">Slot {slotNumber}</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Sélectionnez les lots Conthey à sortir. Vous pouvez mixer plusieurs DLC. Le pré-remplissage est FIFO — modifiable.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        ) : (
          <>
            <div className="flex items-center gap-3 flex-wrap text-sm border-b pb-3">
              <div>
                Objectif : <span className="font-mono font-bold text-[#1F4E3D]" data-testid="lot-dialog-target">{qtyTarget}</span> barquette{qtyTarget > 1 ? "s" : ""}
              </div>
              <div>
                Choisi : <span className={`font-mono font-bold ${totalPicked === qtyTarget ? "text-[#1F4E3D]" : "text-[#C05C47]"}`} data-testid="lot-dialog-total">{totalPicked}</span>
              </div>
              {missing > 0 && <Badge className="bg-[#C05C47] text-white">Manque {missing}</Badge>}
              {overShoot > 0 && <Badge className="bg-[#8C6B2F] text-white">Excès {overShoot}</Badge>}
              <Button variant="outline" size="sm" onClick={applyFifoAuto} data-testid="lot-dialog-fifo-btn">
                <Zap className="w-3.5 h-3.5 mr-1.5" /> FIFO auto
              </Button>
            </div>

            {lots.length === 0 ? (
              <div className="py-6 text-center text-[#575A57]">
                <AlertTriangle className="w-6 h-6 inline mr-2 text-[#8C6B2F]" />
                Aucun lot disponible pour ce produit à Conthey.
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto border border-[#E5E5E0] rounded">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAFAF7] text-[#575A57] text-xs uppercase sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Lot</th>
                      <th className="text-left px-3 py-2">DLC</th>
                      <th className="text-right px-3 py-2">Dispo</th>
                      <th className="text-right px-3 py-2 w-24">À prendre</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lots.map((lot) => {
                      const pickedQty = Number(picks[lot.lot_id] || 0);
                      const isActive = pickedQty > 0;
                      return (
                        <tr
                          key={lot.lot_id}
                          className={`border-t border-[#E5E5E0] ${isActive ? "bg-[#EAF4EE]" : ""}`}
                          data-testid={`lot-dialog-row-${lot.lot_number || lot.lot_id.slice(0, 8)}`}
                        >
                          <td className="px-3 py-2 font-mono text-xs">{lot.lot_number || lot.lot_id.slice(0, 8)}</td>
                          <td className="px-3 py-2">
                            <span className="font-mono text-xs">{fmtDlc(lot.dlc)}</span>
                            {lot.is_dlc_short && (
                              <Badge className="ml-2 bg-[#C05C47] text-white text-[10px]">DLC courte</Badge>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{lot.quantity.toFixed(0)}</td>
                          <td className="px-3 py-2 text-right">
                            <Input
                              type="number"
                              min="0"
                              max={lot.quantity}
                              step="1"
                              value={pickedQty}
                              onChange={(e) => setLotQty(lot.lot_id, e.target.value, lot.quantity)}
                              className="w-20 text-right font-mono h-9"
                              data-testid={`lot-dialog-qty-${lot.lot_number || lot.lot_id.slice(0, 8)}`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button
            onClick={handleConfirm}
            disabled={loading}
            className="bg-[#1F4E3D] hover:bg-[#173D2F]"
            data-testid="lot-dialog-confirm-btn"
          >
            Valider ({totalPicked})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
